import cn from "@/v2-ui/utils/utils.cn"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"

export type LicenseFeatureGroupPresetMenuProps = {
  /** What the menu sets, for the labels: "every map layer", "everything in Property". */
  subject: string
  /**
   * The other packages that carry anything here, biggest first, with how
   * much — the "copy from" presets.
   */
  coverage: readonly (readonly [name: string, count: number])[]
  total: number
  onEverything(): void
  onNothing(): void
  onSameAs(packageName: string): void
}

// The quick ways to set a whole card while composing a package: grant it
// all as a rule, clear it, or copy exactly what another package carries
// here. A tiered catalog is composed as deltas — Standard is Basic plus a
// few tabs — and copying a sibling's picks is how an operator starts that,
// instead of ticking fifteen rows twice. Each item says what it will do,
// because "Same as Basic" on its own did not. The checkboxes stay the fine
// control; this is the coarse one, next to them. Package lens only: on a
// customer's own list there is no sibling to copy from, and the header tick
// already covers "everything" and "nothing".
function LicenseFeatureGroupPresetMenu(
  props: LicenseFeatureGroupPresetMenuProps,
) {
  const { subject, coverage, total, onEverything, onNothing, onSameAs } = props
  return (
    <ActionMenu>
      <ActionMenu.Trigger
        size="xs"
        iconSrc="caret-down"
        className="shrink-0 text-primary hover:text-primary"
        content={`Apply one choice to all of ${subject} at once`}
        label={`Apply one choice to all of ${subject} at once`}
        data-feature-code="UI_BackofficeFeatureGroup_Preset_Trigger"
      >
        Apply to All
      </ActionMenu.Trigger>
      <ActionMenu.Content align="end" className="min-w-72">
        <ActionMenu.Item
          iconSrc="check"
          variant="success"
          data-feature-code="UI_BackofficeFeatureGroup_Preset_Everything"
          onSelect={onEverything}
        >
          <span className="flex flex-col">
            <span>Grant everything in {subject}</span>
            <span className="text-[11px] text-muted-foreground">
              as a rule: what a later release adds is included too
            </span>
          </span>
        </ActionMenu.Item>
        <ActionMenu.Item
          iconSrc="cross"
          variant="destructive"
          data-feature-code="UI_BackofficeFeatureGroup_Preset_Nothing"
          onSelect={onNothing}
        >
          <span className="flex flex-col">
            <span>Grant nothing in {subject}</span>
            <span className="text-[11px] text-muted-foreground">
              clears the rule and every tick in this card
            </span>
          </span>
        </ActionMenu.Item>
        {coverage.length > 0 && (
          <>
            <ActionMenu.Separator />
            <ActionMenu.Label>
              Copy another package&apos;s ticks in {subject}
            </ActionMenu.Label>
            {coverage.map(([name, count]) => (
              <ActionMenu.Item
                key={name}
                iconSrc="copy"
                data-feature-code="UI_BackofficeFeatureGroup_Preset_SameAs"
                onSelect={() => {
                  onSameAs(name)
                }}
              >
                Same as {name}
                {/* How much of the card that package covers, as a bar and
                    a number: the operator picks the sibling to start from
                    by eye, before reading. */}
                <span className="ml-auto flex items-center gap-2 pl-3">
                  <span
                    className="h-1.5 w-14 overflow-hidden rounded-full bg-muted"
                    aria-hidden
                  >
                    <span
                      className={cn(
                        "block h-full rounded-full",
                        count === total ? "bg-primary" : "bg-primary/60",
                      )}
                      style={{ width: `${Math.round((count / total) * 100)}%` }}
                    />
                  </span>
                  <span
                    className={cn(
                      "w-14 text-right text-[11px] tabular-nums",
                      count === total
                        ? "text-primary"
                        : "text-muted-foreground",
                    )}
                  >
                    {count === total ? "all" : `${count} of ${total}`}
                  </span>
                </span>
              </ActionMenu.Item>
            ))}
          </>
        )}
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicenseFeatureGroupPresetMenu
